import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Play, RefreshCw, ListChecks, CheckCircle2, XCircle } from 'lucide-react';

interface TestCaseRow {
  id?: string;
  posit_id: string;
  name: string;
  prompt: string;
  expected_mode?: 'strong' | 'weak' | 'none';
  require_evidence?: boolean;
  max_latency_ms?: number;
}

interface ExecutionRow {
  posit_id: string;
  case_id?: string;
  prompt: string;
  response: string;
  selected_mode: 'strong' | 'weak' | 'none';
  citations_count: number;
  pinecone_results: number;
  max_score: number;
  response_time_ms: number;
  passed: boolean;
  reasons: string[];
}

export default function BatchChatTesting() {
  const { user } = useAuth();
  const [posits, setPosits] = useState<{ id: string; title: string }[]>([]);
  const [selectedPosit, setSelectedPosit] = useState<string>('');
  const [testCases, setTestCases] = useState<TestCaseRow[]>([]);
  const [executions, setExecutions] = useState<ExecutionRow[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [concurrency, setConcurrency] = useState(3);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('posits').select('id,title').limit(200);
      setPosits(data || []);
    })();
  }, []);

  const addCase = () => {
    if (!selectedPosit) return;
    setTestCases(prev => ([...prev, { posit_id: selectedPosit, name: `Case ${prev.length + 1}`, prompt: '', expected_mode: undefined, require_evidence: true, max_latency_ms: 5000 }]));
  };

  const autoGenerateCases = async () => {
    if (!selectedPosit) return;
    setIsGenerating(true);
    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || SUPABASE_ANON_KEY;

      // Prefer new test-case generator; fall back to legacy starter questions
      const tryNew = await fetch(`${SUPABASE_URL}/functions/v1/generate-batch-test-cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ positId: selectedPosit, userId: user?.id || null })
      });
      if (tryNew.ok) {
        const json = await tryNew.json();
        if (Array.isArray(json?.cases) && json.cases.length > 0) {
          const mapped: TestCaseRow[] = json.cases.slice(0, 6).map((c: any, i: number) => ({
            posit_id: selectedPosit,
            name: `Auto ${i + 1}`,
            prompt: String(c.text || ''),
            expected_mode: (c.intended_mode === 'strong' || c.intended_mode === 'weak' || c.intended_mode === 'none') ? c.intended_mode : undefined,
            require_evidence: true,
            max_latency_ms: 5000,
          }));
          setTestCases(mapped);
          return;
        }
      }

      const legacy = await fetch(`${SUPABASE_URL}/functions/v1/generate-starter-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ positId: selectedPosit, userId: user?.id || null, format: 'simple', includeAnalysis: false, forceRegenerate: true })
      });
      const legacyJson = await legacy.json();
      const qs: string[] = legacyJson?.questions || [];
      if (Array.isArray(qs) && qs.length > 0) {
        const newCases: TestCaseRow[] = qs.map((q, i) => ({
          posit_id: selectedPosit,
          name: `Auto ${i + 1}`,
          prompt: q,
          expected_mode: undefined,
          require_evidence: true,
          max_latency_ms: 5000,
        }));
        setTestCases(newCases);
      }
    } catch (e) {
      // no-op UI error; keep silent
    } finally {
      setIsGenerating(false);
    }
  };

  const runSuite = async () => {
    setIsRunning(true);
    setExecutions([]);
    const queue = [...testCases];
    const workers: Promise<void>[] = [];

    const runOne = async (tc: TestCaseRow) => {
      const started = performance.now();
      let fullText = '';
      try {
        // Resolve Supabase URL and auth
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token || SUPABASE_ANON_KEY;

        const res = await fetch(`${SUPABASE_URL}/functions/v1/chat-with-posit-v4-unified`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            // apikey is required by Supabase Functions when called directly
            'apikey': SUPABASE_ANON_KEY,
            // Ensure we receive SSE stream
            'accept': 'text/event-stream',
          },
          body: JSON.stringify({ 
            query: tc.prompt, 
            posit_id: tc.posit_id, 
            user_id: user?.id || null,
            stream: true,
            // Skip gating/preview limits in admin batch tests
            test_mode: true
          })
        });
        if (!res.ok || !res.body) throw new Error('AI service temporarily unavailable');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const payload = line.slice(6).trim();
            if (payload === '[DONE]') break;
            try {
              const data = JSON.parse(payload);
              if (data.chunk) fullText += data.chunk;
              if (data.final) {
                const meta = data.metadata || {};
                const citations = (data.citations || []) as any[];
                const selected_mode = meta.selected_mode || 'none';
                const pinecone_results = meta.pinecone_results || 0;
                const max_score = meta.max_score || 0;
                const response_time_ms = Math.round(performance.now() - started);
                const citations_count = citations.length;

                const reasons: string[] = [];
                let passed = true;
                if (tc.expected_mode && tc.expected_mode !== selected_mode) { passed = false; reasons.push(`mode expected ${tc.expected_mode} got ${selected_mode}`); }
                if (tc.require_evidence && selected_mode === 'strong' && citations_count === 0) { passed = false; reasons.push('expected citations'); }
                if (tc.max_latency_ms && response_time_ms > tc.max_latency_ms) { passed = false; reasons.push(`slow: ${response_time_ms}ms`); }

                const exec: ExecutionRow = { posit_id: tc.posit_id, case_id: tc.id, prompt: tc.prompt, response: fullText, selected_mode, citations_count, pinecone_results, max_score, response_time_ms, passed, reasons };
                setExecutions(prev => [exec, ...prev]);

                // Persist best-effort
                try {
                  await supabase.from('test_executions').insert([{
                    test_case_id: tc.id || null,
                    executor: 'batch-admin',
                    status: passed ? 'passed' : 'failed',
                    input_data: { prompt: tc.prompt },
                    output_data: { response: fullText, citations, metadata: meta },
                    response_time_ms,
                    quality_score: passed ? 9 : 5,
                    full_conversation: [{ role: 'user', content: tc.prompt }, { role: 'assistant', content: fullText }]
                  }]);
                } catch {}
              }
            } catch {}
          }
        }
      } catch (e) {
        // Fallback: non-stream invocation via supabase-js to avoid CORS/SSE issues
        try {
          const { data, error } = await supabase.functions.invoke('chat-with-posit-v4-unified', {
            body: {
              query: tc.prompt,
              posit_id: tc.posit_id,
              user_id: user?.id || null,
              test_mode: true,
              stream: false
            },
          });
          const response_time_ms = Math.round(performance.now() - started);
          if (error) throw error;
          const meta = data?.metadata || {};
          const selected_mode = meta.selected_mode || 'none';
          const citations = Array.isArray(data?.citations) ? data.citations : [];
          const citations_count = citations.length;
          const pinecone_results = meta.pinecone_results || 0;
          const max_score = meta.max_score || 0;
          fullText = String(data?.content || '');
          const reasons: string[] = [];
          let passed = true;
          if (tc.expected_mode && tc.expected_mode !== selected_mode) { passed = false; reasons.push(`mode expected ${tc.expected_mode} got ${selected_mode}`); }
          if (tc.require_evidence && selected_mode === 'strong' && citations_count === 0) { passed = false; reasons.push('expected citations'); }
          if (tc.max_latency_ms && response_time_ms > tc.max_latency_ms) { passed = false; reasons.push(`slow: ${response_time_ms}ms`); }
          const exec: ExecutionRow = { posit_id: tc.posit_id, case_id: tc.id, prompt: tc.prompt, response: fullText, selected_mode, citations_count, pinecone_results, max_score, response_time_ms, passed, reasons };
          setExecutions(prev => [exec, ...prev]);
          // Persist best-effort
          try {
            await supabase.from('test_executions').insert([{
              test_case_id: tc.id || null,
              executor: 'batch-admin',
              status: passed ? 'passed' : 'failed',
              input_data: { prompt: tc.prompt },
              output_data: { response: fullText, citations, metadata: meta },
              response_time_ms,
              quality_score: passed ? 9 : 5,
              full_conversation: [{ role: 'user', content: tc.prompt }, { role: 'assistant', content: fullText }]
            }]);
          } catch {}
        } catch (e2) {
          const response_time_ms = Math.round(performance.now() - started);
          const exec: ExecutionRow = { posit_id: tc.posit_id, prompt: tc.prompt, response: `error: ${String(e)} | fallback: ${String(e2)}`, selected_mode: 'none', citations_count: 0, pinecone_results: 0, max_score: 0, response_time_ms, passed: false, reasons: ['error'] };
          setExecutions(prev => [exec, ...prev]);
        }
      }
    };

    for (let i = 0; i < concurrency; i++) {
      workers.push((async () => {
        while (queue.length > 0) {
          const tc = queue.shift();
          if (tc) await runOne(tc);
        }
      })());
    }

    await Promise.all(workers);
    setIsRunning(false);
  };

  const passRate = useMemo(() => {
    if (executions.length === 0) return 0;
    return Math.round((executions.filter(e => e.passed).length / executions.length) * 100);
  }, [executions]);

  return (
    <DashboardLayout title="Posit Performance Test" description="Batch run prompts against a Posit with pass/fail checks" maxWidth="7xl">
      <div className="space-y-6">
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Batch Chat Testing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
            <div className="lg:col-span-2 min-w-0">
              <label className="text-sm font-medium">Select Posit</label>
              <Select onValueChange={setSelectedPosit} value={selectedPosit}>
                <SelectTrigger><SelectValue placeholder="Choose a posit" /></SelectTrigger>
                <SelectContent>
                  {posits.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium">Concurrency</label>
              <Input type="number" min={1} max={10} value={concurrency} onChange={e => setConcurrency(Number(e.target.value))} />
            </div>
            <div className="lg:col-span-1 flex flex-wrap justify-end gap-2 min-w-0">
              <Button onClick={addCase} variant="outline" className="whitespace-nowrap"><ListChecks className="w-4 h-4 mr-2" />Add Case</Button>
              <Button onClick={autoGenerateCases} variant="outline" disabled={!selectedPosit || isGenerating} className="whitespace-nowrap">
                <ListChecks className="w-4 h-4 mr-2" />Auto-generate Cases
              </Button>
              <Button onClick={runSuite} disabled={isRunning || testCases.length === 0 || testCases.some(c => !c.prompt.trim())} className="whitespace-nowrap"><Play className="w-4 h-4 mr-2" />Run Suite</Button>
              <Button onClick={() => setExecutions([])} variant="secondary" className="whitespace-nowrap"><RefreshCw className="w-4 h-4 mr-2" />Clear</Button>
            </div>
          </div>

          <div className="space-y-3">
            {testCases.map((tc, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 border rounded-md p-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground">Name</label>
                  <Input value={tc.name} onChange={e => setTestCases(prev => prev.map((x, i) => i===idx?{...x, name:e.target.value}:x))} />
                </div>
                <div className="md:col-span-5">
                  <label className="text-xs text-muted-foreground">Prompt</label>
                  <Textarea rows={2} value={tc.prompt} onChange={e => setTestCases(prev => prev.map((x, i) => i===idx?{...x, prompt:e.target.value}:x))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground">Expected Mode</label>
                  <Select value={tc.expected_mode} onValueChange={(v: any) => setTestCases(prev => prev.map((x, i) => i===idx?{...x, expected_mode:v}:x))}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strong">strong</SelectItem>
                      <SelectItem value="weak">weak</SelectItem>
                      <SelectItem value="none">none</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1">
                  <label className="text-xs text-muted-foreground">Require Evidence</label>
                  <Select value={String(tc.require_evidence)} onValueChange={(v: any)=> setTestCases(prev => prev.map((x,i)=> i===idx?{...x, require_evidence: v==='true'}:x))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground">Max Latency (ms)</label>
                  <Input type="number" value={tc.max_latency_ms || 5000} onChange={e => setTestCases(prev => prev.map((x,i)=> i===idx?{...x, max_latency_ms:Number(e.target.value)}:x))} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Results <Badge>{passRate}% pass</Badge></CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Posit</TableHead>
                <TableHead>Prompt</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Citations</TableHead>
                <TableHead>Max Score</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.map((ex, i) => (
                <TableRow key={i}>
                  <TableCell>{posits.find(p=>p.id===ex.posit_id)?.title || ex.posit_id}</TableCell>
                  <TableCell className="max-w-[420px] truncate" title={ex.prompt}>{ex.prompt}</TableCell>
                  <TableCell><Badge>{ex.selected_mode}</Badge></TableCell>
                  <TableCell>{ex.citations_count}</TableCell>
                  <TableCell>{ex.max_score.toFixed(2)}</TableCell>
                  <TableCell>{ex.response_time_ms} ms</TableCell>
                  <TableCell>{ex.passed ? <Badge className="bg-green-600"><CheckCircle2 className="w-4 h-4 mr-1" />pass</Badge> : <Badge variant="destructive"><XCircle className="w-4 h-4 mr-1" />fail</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </div>
    </DashboardLayout>
  );
}


